<template>
  <v-dialog
    width="600"
    v-model="opened"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    @keydown.esc="$emit('update:opened', false)"
    persistent
    lazy
  >
    <v-card>
      <v-card-title class="pb-1">
        <sc-h3>Signature Pad</sc-h3>
      </v-card-title>

      <v-tabs fixed-tabs v-model="active">
        <v-tab :disabled="forceTypeSignature" class="text-capitalize"
          >Draw</v-tab
        >
        <v-tab class="text-capitalize" :disabled="forceDrawSignature"
          >Type</v-tab
        >
      </v-tabs>

      <v-tabs-items v-model="active">
        <v-tab-item>
          <v-layout row justify-center class="signature-pad-container">
            <VueSignaturePad
              class="signature-pad"
              width="560px"
              height="92px"
              ref="signaturePadRef"
              :options="options"
            />
          </v-layout>
        </v-tab-item>

        <v-tab-item>
          <v-layout row justify-center>
            <v-text-field
              class="signature"
              :value="textSignature"
              ref="signatureInputRef"
              placeholder="Type signature here"
              @input="$emit('update:textSignature', $event)"
            />
          </v-layout>
        </v-tab-item>
      </v-tabs-items>

      <v-card-actions>
        <v-btn
          outline
          class="primary--text text-none"
          @click="clearSignature()"
          v-if="active === 0"
        >
          Clear
        </v-btn>
        <v-spacer />
        <v-btn
          :disabled="!canSave"
          :loading="saving"
          class="primary text-none"
          @click="saveSignature()"
        >
          Sign
        </v-btn>
        <v-btn
          class="error--text text-none"
          flat
          @click="$emit('update:opened', false)"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';

const DATA_URL_PREFIX = 'data:image/png;base64,';
const MIN_BASE64_LENGTH = 100;

function isValidImageSignature(data) {
  if (data == null || typeof data !== 'string') {
    return false;
  }
  const trimmed = data.trim();
  if (trimmed === '' || trimmed === '.') {
    return false;
  }
  if (!trimmed.startsWith(DATA_URL_PREFIX)) {
    return false;
  }
  const base64 = trimmed.slice(DATA_URL_PREFIX.length).trim();
  if (base64.length < MIN_BASE64_LENGTH) {
    return false;
  }
  try {
    atob(base64);
  } catch (_) {
    return false;
  }
  return true;
}

export default {
  name: 'signturePadDialog',

  props: {
    opened: {
      type: Boolean,
    },

    saving: {
      type: Boolean,
    },

    signatureType: {
      type: String,
    },

    imageSignature: {
      type: String,
    },

    imageRawData: {
      type: Array,
    },

    textSignature: {
      type: String,
    },
  },

  mixins: [FirmPreferenceMixins],

  data() {
    return {
      active: this.signatureType === 'image' ? 0 : 1,
      isInternalUpdate: false,
      options: {
        penColor: '#000',
        onEnd: this.setImageSignature,
      },
    };
  },

  methods: {
    clearSignature() {
      if (this.$refs.signaturePadRef) {
        this.$refs.signaturePadRef.clearSignature();
      }
      this.$emit('update:imageSignature', '');
    },

    saveSignature() {
      if (this.active === 0 && !this.forceTypeSignature) {
        const pad = this.$refs.signaturePadRef;
        if (!pad) return;

        const { data } = pad.saveSignature();

        if (!isValidImageSignature(data)) {
          this.$emit('update:imageSignature', '');
          this.$store.dispatch('application/setErrorNotification', {
            message: "Signature couldn't be saved; try signing again",
          });
          return;
        }

        const rawData = pad.toData();

        this.$emit('update:signatureType', 'image');
        this.$emit('update:imageSignature', data);
        this.$emit('update:imageRawData', rawData);
        this.$emit('saveSignature');
      } else {
        this.$emit('update:signatureType', 'text');
        this.$emit('saveSignature');
      }
    },

    setImageSignature() {
      const { data } = this.$refs.signaturePadRef.saveSignature();
      this.isInternalUpdate = true;
      this.$emit(
        'update:imageSignature',
        isValidImageSignature(data) ? data : '',
      );
    },
  },

  computed: {
    canSave() {
      if (this.active === 0 && !this.forceTypeSignature) {
        return isValidImageSignature(this.imageSignature);
      }
      if (this.active === 1 && !isEmpty(this.textSignature)) {
        return true;
      }
      return false;
    },
  },

  watch: {
    active: {
      handler(value) {
        if (value === 1) {
          this.$refs.signatureInputRef.focus();
        }
        this.$emit('update:signatureType', value === 0 ? 'image' : 'text');
      },
    },

    imageSignature: {
      handler(value) {
        if (this.isInternalUpdate) {
          this.isInternalUpdate = false;
          return;
        }
        if (isEmpty(value) || !isValidImageSignature(value)) {
          this.clearSignature();
        } else if (this.$refs.signaturePadRef) {
          this.$refs.signaturePadRef.fromDataURL(value);
        }
      },
    },

    signatureType: {
      handler(value) {
        if (value === 'image') {
          this.active = 0;
        } else {
          this.active = 1;
        }
      },
    },
  },

  mounted() {
    if (
      this.$refs.signaturePadRef &&
      isValidImageSignature(this.imageSignature)
    ) {
      this.$refs.signaturePadRef.fromDataURL(this.imageSignature);
    }
    if (this.forceTypeSignature) {
      this.active = 1;
    }
  },
};
</script>

<style lang="postcss" scoped>
.signature-pad-container {
  background: #eee;
  padding: 20px;
}
.signature-pad {
  background: #fff;
}
.signature {
  font-family: 'Zeyada', cursive;
  font-size: 32px;
  max-width: 300px;

  & >>> input {
    max-height: 50px;
    text-align: center;
  }
}
</style>
